import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import type { SharedPlatform } from '../components/social/socialTypes';

const STORAGE_KEY = 'petso_social_connections';

interface SocialConnectionsContextValue {
  connectedPlatforms: SharedPlatform[];
  isConnected: (platform: SharedPlatform) => boolean;
  connectPlatform: (platform: SharedPlatform) => void;
  disconnectPlatform: (platform: SharedPlatform) => void;
}

const SocialConnectionsCtx =
  createContext<SocialConnectionsContextValue | null>(null);

function loadFromStorage(): SharedPlatform[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((p) =>
      ['instagram', 'facebook', 'tiktok', 'x'].includes(p)
    ) as SharedPlatform[];
  } catch {
    return [];
  }
}

function saveToStorage(platforms: SharedPlatform[]) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(platforms));
  } catch {
    // ignore
  }
}

export function SocialConnectionsProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [connectedPlatforms, setConnectedPlatforms] =
    useState<SharedPlatform[]>(loadFromStorage);

  const isConnected = useCallback(
    (platform: SharedPlatform) => connectedPlatforms.includes(platform),
    [connectedPlatforms]
  );

  const connectPlatform = useCallback((platform: SharedPlatform) => {
    setConnectedPlatforms((prev) => {
      if (prev.includes(platform)) return prev;
      const next = [...prev, platform];
      saveToStorage(next);
      return next;
    });
  }, []);

  const disconnectPlatform = useCallback((platform: SharedPlatform) => {
    setConnectedPlatforms((prev) => {
      const next = prev.filter((p) => p !== platform);
      saveToStorage(next);
      return next;
    });
  }, []);

  const value = useMemo<SocialConnectionsContextValue>(
    () => ({
      connectedPlatforms,
      isConnected,
      connectPlatform,
      disconnectPlatform,
    }),
    [connectedPlatforms, isConnected, connectPlatform, disconnectPlatform]
  );

  return (
    <SocialConnectionsCtx.Provider value={value}>
      {children}
    </SocialConnectionsCtx.Provider>
  );
}

export function useSocialConnections(): SocialConnectionsContextValue {
  const ctx = useContext(SocialConnectionsCtx);
  if (!ctx) {
    return {
      connectedPlatforms: [],
      isConnected: () => false,
      connectPlatform: () => {},
      disconnectPlatform: () => {},
    };
  }
  return ctx;
}
